import { useEffect, useState } from 'react';

import colors from '@atom/styles/colors';
import { WorkOrderField } from '@atom/types/work';
import { toggleFromSet } from '@atom/utilities/setUtilities';
import {
  CustomFieldView,
  getTaskFieldWidth,
} from '@atom/utilities/taskUtilities';
import { getWorkOrderMultiSelectTitle } from '@atom/utilities/workOrderFieldUtilities';

import classes from './workOrderFields.module.css';

type WorkOrderEnumMultipleFieldListProps = {
  field: WorkOrderField;
  onChange: (fieldId: string, value: any) => void;
  isDisabled?: boolean;
  customFieldView: CustomFieldView;
  requiredFieldIds?: Set<string>;
  requiredFieldName?: keyof WorkOrderField;
  // Overrides `requiredFieldName` when more than one requirement applies
  requiredFieldNames?: (keyof WorkOrderField)[];
  isTemplate?: boolean;
  onClick?: () => void;
  width?: string;
  dataCy?: string;
  error?: boolean;
  helperText?: string;
};

const WorkOrderEnumMultipleFieldList = ({
  field,
  onChange,
  isDisabled,
  customFieldView,
  requiredFieldIds = new Set([]),
  requiredFieldName = 'requiredOnCreate',
  requiredFieldNames,
  isTemplate,
  onClick,
  width,
  dataCy,
  error,
  helperText,
}: WorkOrderEnumMultipleFieldListProps) => {
  const [values, setValues] = useState<Set<string>>(
    new Set(field?.value || []),
  );
  const baseDataCy = dataCy ?? `workInfoField-${field.title}-EnumMultipleList`;

  const toggleCheck = (option: string) => {
    if (isDisabled) {
      return;
    }
    const newValues = toggleFromSet(values, option);
    setValues(newValues);
    onChange(field.id, [...newValues]);
  };

  useEffect(() => {
    setValues(new Set(field?.value || []));
  }, [field?.value]);

  const fieldWidth = width ?? getTaskFieldWidth(customFieldView);

  const labelColor =
    error || requiredFieldIds.has(field.id)
      ? colors.brand.red
      : colors.neutral.dim;

  // template displays requirement on required or requiredOnCreate
  const text = getWorkOrderMultiSelectTitle(
    field,
    requiredFieldNames ??
      (isTemplate ? ['requiredOnCreate', 'required'] : [requiredFieldName]),
  );

  return (
    <div onClick={onClick} data-cy={baseDataCy}>
      <div
        className={classes.fieldTitle}
        style={{
          color: labelColor,
          maxWidth: fieldWidth,
        }}
      >
        {text}
      </div>
      <div className={classes.listContainer}>
        {field.enumeration.map(option => (
          <div key={option} className={classes.listItem}>
            <div className={classes.listInputContainer}>
              <input
                className={classes.listInput}
                type="checkbox"
                onClick={() => toggleCheck(option)}
                checked={values.has(option)}
                disabled={isDisabled}
                data-cy={`${baseDataCy}-option-${option}`}
              />
            </div>
            <div onClick={() => toggleCheck(option)}>{option}</div>
          </div>
        ))}
      </div>
      {error && helperText && (
        <div className={classes.listHelperText}>{helperText}</div>
      )}
    </div>
  );
};

export default WorkOrderEnumMultipleFieldList;
